import { CSSProperties, useEffect, useState } from 'react';
import { Box, Button, ClickAwayListener, styled } from '@material-ui/core';
import {
  SText,
  SSelectDropdown,
  SelectDetailsButton,
  SEmptyState,
  SInfiniteScroll,
  SingleSelectLoader,
  SSearchInput,
  SButton
} from '@setvi/shared/components';
import { LocationPointIcon } from '@setvi/shared/components/sicons';
import CloseIcon from '@material-ui/icons/Close';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import eventEmitter from '@setvi/shared/libs/EventEmitter';
import { distances } from '@setvi/shared/styles';
import { TextColor } from '@setvi/shared/enums';
import { CustomerPreferencesInterface } from './interfaces';
import { useCustomerPreferences } from './hook';

const StyledSearchInput = styled(SSearchInput)({
  width: '100%',
  borderRadius: 99,
  margin: `${distances.px.small} 0 ${distances.px.medium} 0`,
  background: '#F8F9FA',
  padding: `${distances.px.small} ${distances.px.base}`,
  '& fieldset': {
    borderColor: '#EEF0F2 !important'
  }
});

interface CustomerDropdownProps {
  newDesign?: boolean;
  selected: CustomerPreferencesInterface | null;
  onSelect: (customer: CustomerPreferencesInterface) => void;
  style?: { input?: CSSProperties; dropdownWrapperWidth?: number };
}

const CustomerPreferencesDropdown = ({
  newDesign = false,
  selected,
  onSelect,
  style
}: CustomerDropdownProps) => {
  const [anchor, setAnchor] = useState(null);
  const {
    customers,
    isLoading,
    searchQuery,
    searchCustomerPreferences,
    fetchMoreSearchResults
  } = useCustomerPreferences();

  const handleSelectCustomer = (customer: CustomerPreferencesInterface) => {
    onSelect(customer);
    setAnchor(null);
  };

  const isSelected = (customer: CustomerPreferencesInterface) =>
    selected?._id === customer._id;

  useEffect(() => {
    eventEmitter.on('sdropdown-menu-opened', () => setAnchor(null));

    return () => {
      eventEmitter.off('sdropdown-menu-opened', () => setAnchor(null));
    };
  }, []);

  useEffect(() => {
    // Fetch customers on component mount
    searchCustomerPreferences('');
  }, [searchCustomerPreferences]);

  return (
    <ClickAwayListener onClickAway={() => setAnchor(null)}>
      <SSelectDropdown
        newDesign={newDesign}
        anchor={
          <SelectDetailsButton
            newDesign={newDesign}
            width={258}
            style={style?.input}
            noBorder
            title={selected ? selected.vetName : `Select Customer`}
            titleHighligted={!!selected}
            onClick={e => setAnchor(e?.currentTarget)}
            open={Boolean(anchor)}
          />
        }
        disablePortal
        anchorElement={anchor}
        onClose={() => setAnchor(null)}
        content={
          <Box width={style?.dropdownWrapperWidth || 285}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center">
              <SText
                lineHeight="24px"
                weight="semibold"
                title="Select Customer"
              />
              {selected && (
                <SButton
                  variant="text"
                  style={{
                    minHeight: 'max-content',
                    padding: '2px 8px',
                    fontSize: 12
                  }}
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    onSelect(null);
                    setAnchor(null);
                  }}>
                  Clear
                </SButton>
              )}
            </Box>
            <StyledSearchInput
              defaultValue={searchQuery}
              placeholder="Search Customer..."
              searchIconSize="14px"
              onChange={val => searchCustomerPreferences(val)}
            />
            <Box height={300} overflow="auto">
              <Box>
                <SInfiniteScroll
                  list={customers}
                  keyGetter={customer => customer._id}
                  fetchMore={fetchMoreSearchResults}
                  render={customer => (
                    <Button
                      fullWidth
                      style={{
                        display: 'flex',
                        justifyContent: 'start',
                        borderRadius: distances.px.base,
                        padding: `${distances.px.xsmall} ${distances.px.base}`,
                        marginBottom: distances.px.xsmall,
                        background: isSelected(customer) && '#EBF5FF',
                        color: isSelected(customer) ? '#2196F3' : undefined
                      }}
                      onClick={() => handleSelectCustomer(customer)}
                      endIcon={
                        <CheckRoundedIcon
                          htmlColor={
                            isSelected(customer)
                              ? TextColor.Blue
                              : 'transparent'
                          }
                        />
                      }
                      variant="text"
                      color="default">
                      <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        gridGap="4px">
                        <SText
                          title={customer.vetName}
                          fontColor={
                            isSelected(customer) ? TextColor.Blue : '#20262D'
                          }
                          lineHeight="24px"
                          weight="bold"
                        />
                        {customer.cityState && (
                          <Box display="flex" alignItems="center" gridGap="2px">
                            <LocationPointIcon
                              style={{ width: 16, height: 16 }}
                              fill={isSelected(customer) && TextColor.Blue}
                            />
                            <SText
                              size="sm"
                              lineHeight="20px"
                              fontColor={
                                isSelected(customer)
                                  ? TextColor.Blue
                                  : TextColor.SlateGrey
                              }
                              title={customer.cityState}
                            />
                          </Box>
                        )}
                      </Box>
                    </Button>
                  )}
                />
              </Box>
              {isLoading && <SingleSelectLoader />}
              {!isLoading && customers?.length === 0 && (
                <SEmptyState subTitle="No customers found." />
              )}
            </Box>
          </Box>
        }
      />
    </ClickAwayListener>
  );
};

export default CustomerPreferencesDropdown;
